import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizRounded } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { HSpacer } from 'src/components/core/Spacer';
import { View } from 'src/components/core/View';
import { AbstractCode, Coding } from 'src/types/insights';
import { Hidden } from 'src/util/styles';
import { AccordionType, useCodebookContext } from '../Catalog/CodebookProvider';
import CodeChip from '../Common/CodeChip';

import foraColors from '../../../assets/_util.scss';

interface BaseProps<CodeType extends AbstractCode> {
  additionalIconButtons?: JSX.Element;
  codes: CodeType[];
  selectCode: (id: number, selected: boolean) => void;
  selectedCodes: Pick<Coding, 'code_id'>[];
  selecting: boolean;
  type: AccordionType;
}

const CodeAccordionModern = <CodeType extends AbstractCode>(
  props: BaseProps<CodeType>
) => {
  const { codes, selectCode, selectedCodes, selecting, type } = props;
  const {
    codeFilter: filter,
    openDialog,
    setAccordionType,
    setNewChildCodeParentId,
    openMenu,
    showAIReasoning,
    selectedEntry,
    aiOn,
  } = useCodebookContext();
  const { t } = useTranslation();
  const title = t(`insights.${type}_codes`);
  const [open, setOpen] = React.useState(true);

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClickAdd = (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
    selectedCode?: AbstractCode
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setAccordionType(type);
    setNewChildCodeParentId(selectedCode);
    openDialog('create')();
  };

  const filteredCodes = codes.filter(
    (code) => code.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1
  );
  const selectedCodeIds = new Set(selectedCodes.map((c) => c.code_id));

  const hasAIReason = (codeId: number) =>
    aiOn &&
    !!selectedEntry?.merged_codings.some(
      (sc) => sc.code_id === codeId && sc.ai_metadata?.reason
    );

  const ExpanderIcon = open ? ExpandLessIcon : ExpandMoreIcon;
  const headerColor = foraColors.foraPurple;

  return (
    <>
      <ListItemButton
        dense
        disableGutters
        onClick={handleClick}
        sx={{
          minHeight: 45,
          gap: '4px',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: `${foraColors.white} !important`,
          borderBottom: `1px solid ${foraColors.gray400}`,
        }}
      >
        <ExpanderIcon style={{ color: headerColor }} />
        <ListItemText
          primary={title}
          primaryTypographyProps={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: headerColor,
          }}
        />
        {props.additionalIconButtons}
        <Tooltip
          arrow
          placement="left"
          title={t('insights.add_new_value', { value: title.toLowerCase() })}
        >
          <IconButton onClick={handleClickAdd}>
            <AddIcon fontSize="small" htmlColor={headerColor} />
          </IconButton>
        </Tooltip>
      </ListItemButton>

      <Collapse in={open} timeout="auto">
        <List data-testid={`CodeAccordion-${title}`} sx={{ p: 0 }}>
          {filteredCodes.map((code, idx, arr) => (
            <React.Fragment key={code.id}>
              <ListItem
                disableGutters
                sx={{
                  minHeight: 36,
                  p: code.parent_id ? '0 0' : '16px 0 0',
                }}
              >
                <ListItemText
                  sx={{
                    py: 0,
                    m: 0,
                    display: 'flex',
                    ...(code.parentage.length > 2 ? { pl: 2 } : {}),
                  }}
                  disableTypography
                >
                  {/* Only allow tagging of child codes */}
                  {code.parent_id && (
                    <Tooltip
                      arrow
                      placement="left"
                      title={t('insights.toggle_coding')}
                    >
                      <Checkbox
                        checked={selectedCodeIds.has(code.id)}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          selectCode(code.id, event.target.checked);
                        }}
                        size="small"
                        sx={{
                          py: 0,
                          pl: 0,
                          pr: 0.5,
                          ...Hidden(!selecting),
                        }}
                      />
                    </Tooltip>
                  )}
                  {code.parent_id ? (
                    <View
                      onClick={() => showAIReasoning(code.id)}
                      style={{
                        cursor: hasAIReason(code.id) ? 'pointer' : 'auto',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      <CodeChip
                        code={code}
                        suggested={
                          aiOn &&
                          selectedEntry?.merged_codings.some(
                            (c) => c.code_id === code.id && c.is_suggested
                          )
                        }
                        applied={selectedCodeIds.has(code.id)}
                      />
                    </View>
                  ) : (
                    <CodeChip
                      code={code}
                      sx={{
                        px: 0,
                        color: foraColors.foraPurpleDarkest,
                        backgroundColor: 'inherit',
                        fontWeight: 700,
                        fontSize: '16px',
                        border: 'none',
                      }}
                      noBold
                    />
                  )}
                </ListItemText>

                <HSpacer grow width="16px" />

                <ListItemIcon
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <IconButton
                    onClick={({ currentTarget }) =>
                      openMenu(code, currentTarget, type)
                    }
                  >
                    <MoreHorizRounded
                      fontSize="small"
                      htmlColor={foraColors.gray700}
                    />
                  </IconButton>
                </ListItemIcon>
              </ListItem>
              {/*
                If we're editing and we've reached the end of the codes list, or the next code is a parent header,
                display the "New child code" button
              */}
              {(arr[idx + 1] === undefined ||
                arr[idx + 1]?.parent_id === undefined) && (
                <ListItemButton
                  disableGutters
                  sx={{ py: 0, minHeight: 36 }}
                  onClick={(event) => handleClickAdd(event, arr[idx])}
                >
                  <ListItemIcon sx={{ minWidth: 0, pr: 1 }}>
                    <AddIcon fontSize="small" htmlColor={foraColors.gray700} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: foraColors.gray700,
                      }}
                    >
                      {t('insights.child_code_add')}
                    </Typography>
                  </ListItemText>
                </ListItemButton>
              )}
            </React.Fragment>
          ))}
          {filteredCodes.length === 0 && (
            <ListItem disableGutters disabled sx={{ pt: 1 }}>
              <ListItemText sx={{ pl: 1, py: 0, my: 0 }}>
                <Typography variant="body2">
                  {t('insights.empty_codes')}
                </Typography>
              </ListItemText>
            </ListItem>
          )}
        </List>
      </Collapse>
    </>
  );
};
export default CodeAccordionModern;
